import _useTranslation from 'lib/hooks/_useTranslation'
import { Body1, Subtitle2 } from 'stories/elements/Typography/Text'
import BookmarkIcon from 'assets/app/library/bookmark.svg'
import BackArrowIcon from 'assets/icons/back-arrow.svg'
import styled, { css } from 'styled-components'
import theme from 'stories/utils/theme'
import { KnowFolder } from 'interfaces/KnowFolder'
import Link from 'next/link'
import { appLibraryFolderRoute } from 'lib/constants/routes'
import { returnFolderIcon } from 'lib/features/library'
import { useCallback, useEffect, useState } from 'react'
import LibraryService from 'services/LibraryService'
import { ApiError } from 'services/ApiService'
import { Know, KnowFormat } from 'interfaces/Know'
import FlashcardInfoPreview from 'stories/elements/Know/FlashcardInfo'

const FolderContainer = styled.div`
    height: 310px;
    padding: 25px 20px;
    display: grid;
    gap: 20px;
    width: 100%;
    background-color: ${theme.colors.backgroundTransparent};
    border-radius: ${theme.borderRadius.webappCard};
    cursor: pointer;
    :hover {
        background-color: ${theme.colors.grayScalePrimaryTransparent};
    }
    .header {
        display: flex;
        justify-content: space-between;
        .title {
            .icon {
                display: grid;
                place-content: center;
                inset: 2px solid ${theme.colors.backgroundTransparent};
                border-radius: 50%;
            }
            display: flex;
            align-items: center;
            gap: 15px;
            height: 50px;
        }
        .information {
            display: grid;
            gap: 5px;
        }
    }
    .row {
        display: grid;
        gap: 10px;
        grid-template-columns: 1fr 1fr 1fr;
        height: 195px;
        width: 100%;
    }
`

const Card = styled.div<{ thumbnail: string }>`
    height: 195px;
    width: 195px;
    border-radius: ${theme.borderRadius.webappCard};
    background-color: ${theme.colors.backgroundTransparent};
    ${({ thumbnail }) =>
        thumbnail
            ? css`
                z-index: 10;
                background-repeat: no-repeat;
                background-size: cover;
                background-image: ${`url(${thumbnail})`}};
              `
            : ''}
`

const EmptyPreview = styled.div`
    height: 195px;
    width: 195px;
    padding: 15px;
    background: ${theme.colors.deepSeaBlue};
    border-radius: ${theme.borderRadius.webappCard};
    display: grid;
    place-content: center;
    position: relative;
    svg {
        fill: white;
        width: 20px;
        height: 20px;
    }
`

const BackArrow = styled(BackArrowIcon)`
    transform: rotate(180deg);
    -ms-transform: rotate(180deg);
    -webkit-transform: rotate(180deg);

    fill: ${theme.colors.grayScaleSecondary};
`

const KnowBadgeWrapper = styled.div`
    height: 20px;
    background-color: ${theme.colors.backgroundTransparent};
    border-radius: ${theme.borderRadius.small};
    display: flex;
    width: fit-content;
    padding: 5px;
    align-items: center;
    gap: 5px;
    svg path {
        fill: ${theme.colors.grayScaleSecondary};
    }
`

interface KnowBadgeProps {
    count: number
}
export const KnowCountBadge = ({ count }: KnowBadgeProps) => {
    const { t: tCommon } = _useTranslation('common')

    return (
        <KnowBadgeWrapper>
            <BookmarkIcon />
            <Subtitle2
                fontFamily="var(--font-open-sans)"
                fontWeight={400}
                fontSize={13}
                color={theme.colors.grayScaleSecondary}
            >{`${count} ${tCommon(count > 1 ? 'common/multipleKnowsTitle' : 'common/knowsTitle')}`}</Subtitle2>
        </KnowBadgeWrapper>
    )
}

interface FolderProps {
    folder: KnowFolder
}

const THUMBNAIL_KNOW_LIMIT = 3

const Folder = ({ folder }: FolderProps) => {
    const [knows, setKnows] = useState<Know[]>()
    const { t: tCommon } = _useTranslation('common')
    const { type, knowsCount, name, uuid, color } = folder

    const fetchFolderKnows = useCallback(() => {
        LibraryService.getKnowsWithinFolder(uuid)
            .then((data) => {
                setKnows(data.content.slice(0, THUMBNAIL_KNOW_LIMIT))
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'library.getKnowsWithinFolder')
                } else {
                    throw error
                }
            })
    }, [uuid])

    useEffect(() => {
        fetchFolderKnows()
    }, [fetchFolderKnows])

    return (
        <Link href={appLibraryFolderRoute(uuid)}>
            <FolderContainer>
                <div className="header">
                    <div className="title">
                        <div className="icon">{returnFolderIcon(type, color)}</div>
                        <div className="information">
                            <Body1 fontFamily="var(--font-inter)" color={theme.colors.white}>
                                {name}
                            </Body1>
                            <KnowCountBadge count={knowsCount} />
                        </div>
                    </div>
                    <BackArrow />
                </div>
                <div className="row">
                    {knows?.map((know) => (
                        <div key={know.uuid}>
                            {know.knowFormat === KnowFormat?.Flashcards ? (
                                <EmptyPreview key={`preview-${know.uuid}`}>
                                    <FlashcardInfoPreview know={know} />
                                </EmptyPreview>
                            ) : (
                                <Card thumbnail={know.thumbnailMediumUrl} key={`card-${know.uuid}`} />
                            )}
                        </div>
                    ))}
                    {knows &&
                        new Array(THUMBNAIL_KNOW_LIMIT - knows?.length)
                            .fill('')
                            .map((_, i) => <Card thumbnail={''} key={i} />)}
                </div>
            </FolderContainer>
        </Link>
    )
}

export default Folder
